import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSnackbar } from "notistack";
import { CircularProgress } from "@mui/material";
import PhonelinkRingIcon from "@mui/icons-material/PhonelinkRing";
import PhonelinkEraseIcon from "@mui/icons-material/PhonelinkErase";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useSocket } from "../../hooks/useSocket";
import { useStore } from "../../context/StoreContext";

const getStoreState = (s) => {
  if (!s)
    return {
      label: "No store selected",
      cls: "bg-secondary",
      hint: "Choose a store on the Stores page",
    };
  if (s.isApproved === false)
    return {
      label: "Awaiting approval",
      cls: "bg-warning text-dark",
      hint: "This store has not been approved yet",
      fix: true,
    };
  if (!s.isActive)
    return {
      label: "Not active",
      cls: "bg-danger",
      hint: "Orders won't sync until the store is activated",
      fix: true,
    };
  return {
    label: "Active",
    cls: "bg-success",
    hint: "Store is active and approved",
  };
};

const LiveStatus = ({ setUnread, canManage = true }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { socket, status = "disconnected", error, retry } = useSocket() || {};
  const { selectedStore, stores } = useStore();

  useEffect(() => {
    if (!socket) return;
    const onAlert = (data) => {
      enqueueSnackbar(`CS Alert: ${data.message} from ${data.from}`, {
        variant: "success",
      });
      setUnread?.((n) => n + 1);
    };
    socket.on("cs_notification", onAlert);
    return () => socket.off("cs_notification", onAlert);
  }, [socket, enqueueSnackbar, setUnread]);

  const connection =
    status === "connecting" ? (
      <CircularProgress size={18} aria-label="Connecting" />
    ) : status === "connected" ? (
      <span
        title="Live updates connected"
        aria-label="Live updates connected"
        className="text-success"
      >
        <PhonelinkRingIcon />
      </span>
    ) : status === "error" ? (
      <span
        title={error || "Connection problem"}
        className="text-danger d-inline-flex align-items-center gap-1"
      >
        <ErrorOutlineIcon />
        {retry && (
          <button
            type="button"
            className="btn btn-sm btn-link text-danger p-0"
            onClick={retry}
          >
            Retry
          </button>
        )}
      </span>
    ) : (
      <span
        title="Live updates off"
        aria-label="Live updates off"
        className="text-muted"
      >
        <PhonelinkEraseIcon />
      </span>
    );

  const state = getStoreState(selectedStore);

  return (
    <span className="d-inline-flex align-items-center gap-2 flex-wrap">
      {connection}
      {selectedStore && (
        <span className="badge bg-primary">{selectedStore.public_name}</span>
      )}
      <span className={`badge ${state.cls}`} title={state.hint}>
        {state.label}
      </span>
      {state.fix &&
        (canManage ? (
          <Link to="/stores" className="small">
            Fix in Stores
          </Link>
        ) : (
          <span className="small text-muted">Ask your workspace owner</span>
        ))}
    </span>
  );
};

export default LiveStatus;
